import { Button, Icon, tokens } from '@hotjar/hotsauce';
import styled from '@emotion/styled';
import { useState, useEffect } from 'react';
import logo from '@hotjar/shared-assets/images/logo-hotjar.svg';
import { useTranslationWithPrefix, useHelpHotjarUrl } from '@hotjar/translations';
import type { State, Step } from './HeaderStep';
import { HeaderStep } from './HeaderStep';
import { SkeletonLoaderOverlay } from '../shared.styles';
import { CONTACT_SUPPORT_LINK } from '~/subscription-management/constants/links';

export type Props<TStepId extends string> = {
  steps: Step<TStepId>[];
  currentStep?: Step;
  onStepClick?: (step: Step<TStepId>) => void;
  loading?: boolean;
};

const S = {
  Container: styled.div`
    height: 72px;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    background-color: ${tokens.color.ui_background};
    box-shadow: inset 0 -1px 0 ${tokens.color.ui_border_light};
    padding: 0 ${tokens.spacer.x2};
  `,
  StepList: styled.ol`
    margin: unset;
    padding: unset;
    list-style: none;
    display: flex;
  `,
  StepsNamesContainer: styled.nav`
    display: flex;
    justify-content: space-around;
    align-items: center;
    margin: auto;
  `,
  Link: styled.a`
    height: 30%;

    &:focus {
      ${tokens.focus()};
    }
  `,
  Logo: styled.img`
    max-height: 100%;
  `,
  ChevronIcon: styled(Icon)`
    margin-inline: ${tokens.spacer.x2};
  `,
};

export function Header<TStepId extends string = string>({
  loading,
  steps,
  currentStep,
  onStepClick,
}: Props<TStepId>): JSX.Element {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const { t } = useTranslationWithPrefix('checkout:header');
  const helpHotjarUrl = useHelpHotjarUrl();

  useEffect(() => {
    setCurrentStepIndex(steps.findIndex((step) => step.id === currentStep?.id));
  }, [currentStep, steps]);

  const getStepState = (index: number): State => {
    if (index > currentStepIndex) {
      return 'disabled';
    }
    if (index < currentStepIndex) {
      return 'done';
    }
    return 'active';
  };

  const clickHandler = (step: Step<TStepId>) => {
    if (getStepState(steps.findIndex((item) => item.id === step.id)) === 'done') {
      onStepClick?.(step);
    }
  };

  return (
    <S.Container>
      <S.Link href="/">
        <S.Logo src={logo} alt={t('home')} />
      </S.Link>
      <S.StepsNamesContainer aria-label={t('navigation-title')} aria-busy={loading}>
        <S.StepList>
          {steps.map((step, index) => (
            <li key={step.id} aria-current={index === currentStepIndex ? 'step' : false}>
              <SkeletonLoaderOverlay isLoading={loading}>
                <HeaderStep
                  step={{ ...step, ...{ state: getStepState(index) } }}
                  onStepClick={clickHandler}
                />
                {index !== steps.length - 1 && (
                  <S.ChevronIcon
                    size="m"
                    icon="chevron_right"
                    color={getStepState(index) === 'active' ? 'text_default' : 'text_disabled'}
                  />
                )}
              </SkeletonLoaderOverlay>
            </li>
          ))}
        </S.StepList>
      </S.StepsNamesContainer>
      <Button kind="secondary" as="a" href={`${helpHotjarUrl}${CONTACT_SUPPORT_LINK}`}>
        {t('help')}
      </Button>
    </S.Container>
  );
}
